import type { InactivityMessageTemplate } from '@coaching/api/db/schema.js';
import { getInactivityDays } from '@coaching/api/utils/inactivity.js';
import {
  isValidWhatsAppLanguageCode,
  normalizeWhatsAppLanguageCode,
} from '@coaching/api/utils/whatsapp-language.js';

export { getInactivityDays };

export interface ParsedInactivityMessageTemplates {
  templates: InactivityMessageTemplate[];
  isInvalid: boolean;
}

export function parseInactivityMessageTemplate(value: unknown): InactivityMessageTemplate | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const template = value as Record<string, unknown>;
  if (typeof template.day !== 'number' || !Number.isInteger(template.day) || template.day <= 0) {
    return null;
  }

  if (typeof template.templateName !== 'string' || template.templateName.trim().length === 0) {
    return null;
  }

  if (template.languageCode !== undefined) {
    if (typeof template.languageCode !== 'string') {
      return null;
    }

    const trimmedLanguageCode = template.languageCode.trim();
    if (!isValidWhatsAppLanguageCode(trimmedLanguageCode)) {
      return null;
    }

    return {
      day: template.day,
      templateName: template.templateName.trim(),
      languageCode: normalizeWhatsAppLanguageCode(trimmedLanguageCode),
      bodyParameters: Array.isArray(template.bodyParameters)
        ? template.bodyParameters
            .filter((parameter): parameter is string => typeof parameter === 'string')
            .map((parameter) => parameter.trim())
            .filter(Boolean)
        : undefined,
    };
  }

  return {
    day: template.day,
    templateName: template.templateName.trim(),
    bodyParameters: Array.isArray(template.bodyParameters)
      ? template.bodyParameters
          .filter((parameter): parameter is string => typeof parameter === 'string')
          .map((parameter) => parameter.trim())
          .filter(Boolean)
      : undefined,
  };
}

export function isInactivityMessageTemplate(value: unknown): value is InactivityMessageTemplate {
  return parseInactivityMessageTemplate(value) !== null;
}

export function parseInactivityMessageTemplatesResult(value: unknown): ParsedInactivityMessageTemplates {
  if (value == null) {
    return { templates: [], isInvalid: false };
  }

  if (!Array.isArray(value)) {
    return { templates: [], isInvalid: true };
  }

  const parsedTemplates: InactivityMessageTemplate[] = [];
  const seenDays = new Set<number>();

  for (const entry of value) {
    const template = parseInactivityMessageTemplate(entry);
    if (!template || seenDays.has(template.day)) {
      return { templates: [], isInvalid: true };
    }

    seenDays.add(template.day);
    parsedTemplates.push(template);
  }

  return { templates: parsedTemplates, isInvalid: false };
}

export function parseInactivityMessageTemplates(value: unknown): InactivityMessageTemplate[] {
  return parseInactivityMessageTemplatesResult(value).templates;
}
